import { Button } from 'app/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'app/components/ui/dialog'
import { BulkResultsSummary } from './BulkResultsSummary'
import type { BulkRowOutcome } from './types'

type BulkResultsModalProps = {
  open: boolean
  title: string
  outcomes: BulkRowOutcome[]
  /**
   * The run was QUEUED, not performed. Every count then reads "scheduled"
   * rather than "succeeded", because "400 succeeded" for a queued send would
   * mean 400 texts went out, and none of them have.
   */
  scheduled?: boolean
  /** When the queued run goes out, e.g. "Tomorrow 8:00 AM". */
  sendsAt?: string
  /**
   * A run-level warning that isn't any one row's fault, e.g. a pre-check the
   * run depends on could not run. Rendered the same way as the "scheduled, not
   * sent yet" notice: an amber banner above the per-row summary, because both
   * are the same kind of fact, something true about the whole run that no
   * single row's reason would say.
   */
  caveat?: string
  onClose: () => void
}

/** Summary of a bulk apply, in its own dialog. */
export function BulkResultsModal({
  open,
  title,
  outcomes,
  scheduled,
  sendsAt,
  caveat,
  onClose,
}: BulkResultsModalProps) {
  const succeeded = outcomes.filter((o) => o.ok)
  const successWord = scheduled ? `scheduled` : `succeeded`

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {scheduled && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-ui-sm text-amber-800">
            Nothing has gone out yet. {succeeded.length === 1 ? `This text sends` : `These texts send`}
            {` `}
            {sendsAt ? `${sendsAt}, ` : ``}on each practice's own clock. You can change or cancel the scheduled send
            from the Scheduled filter in the inbox.
          </p>
        )}

        {caveat && <p className="rounded-md bg-amber-50 px-3 py-2 text-ui-sm text-amber-800">{caveat}</p>}

        <BulkResultsSummary outcomes={outcomes} successWord={successWord} />

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
